import {
  GuestType,
  Payment,
  Price,
  Production,
  Reservation,
  Seat,
  SeatType,
  Staff,
  Stage,
} from "@/graphql/types";
import {
  GuestInfo,
  SelectedValues,
  SpecialGroupDetail,
} from "@/lib/components/reservation/index.d";
import Confirm from "@/lib/components/reservation/confirm";
import GuestInformation from "@/lib/components/reservation/guestInformation";
import {
  CheckAuthToken,
  CreateVipReservation,
  GetAllSeatsOfStage,
  GetReservationInitialData,
  SubmitReservation,
} from "@/graphql/documentNode";
import * as GraphQLClient from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import { useQuery } from "react-query";
import WhoGonnaSitWhere from "@/lib/components/reservation/whoGonnaSitWhere";
import styled from "styled-components";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import { Step, StepButton, Stepper } from "@mui/material";
import Complete from "@/lib/components/reservation/complete";
import { useParams, useSearchParams } from "react-router-dom";
import LoadingIcon from "@/lib/components/common/loadingIcon";
import { useSnackBar } from "@/lib/ts/hooks/useSnackBar";
import SeatSelectArea from "@/lib/components/reservation/seatSelectArea";
import { getDefaultSeatTypes } from "@/lib/ts/admin/defaultSeatTypes";

const NewReservation = () => {
  dayjs.extend(utc)
  dayjs.extend(timezone)
  dayjs.tz.setDefault('Asia/Tokyo');

  const snackBar = useSnackBar({})

  const pathParams = useParams<{ productionId: string, stageId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const serial = searchParams.get("serial")
  const [isVip, setIsVip] = useState(false)

  const productionId = pathParams.productionId
  const stageId = pathParams.stageId ? parseInt(pathParams.stageId) : undefined

  const [loading, setLoading] = useState(false)

  const [production, setProduction] = useState(
    undefined as undefined | Production,
  );
  
  const [productionName, setProductionName] = useState("")
  const [seats, setSeats] = useState<Seat[]>([])
  const [seatTypes, setSeatTypes] = useState<SeatType[]>([])
  const [guestTypes, setGuestTypes] = useState<GuestType[]>([])
  const [prices, setPrices] = useState<Price[]>([])
  const [staffs, setStaffs] = useState<Staff[]>([])
  const [remainingSeats, setRemainingSeats] = useState(0)
  const [payments, setPayments] = useState<Payment[]>([])
  const [stage, setStage] = useState<Stage | undefined>(undefined)

  const [selectedSeatIndexes, setSelectedSeatIndexes] = useState<number[]>([])


  const [specialGroupDetails, setSpecialGroupDetails] = useState<{ type: number; name: string; size: number }[]>([]);

  const [reservation, setReservation] = useState(undefined as undefined | Reservation)

  const [checkAuthToken] = GraphQLClient.useLazyQuery(CheckAuthToken, {
    onCompleted: (data) => {
      setIsVip(data.checkAuthToken);
      if(!data.checkAuthToken) {
        alert("URLが正しくないか、既に座席指定済です。通常の予約画面を表示します。")
      }
    },
  });

  useQuery([serial], async () => {
    if(serial) {
      await checkAuthToken({
        variables: {
          input: {
            token: serial,
            stageId: typeof stageId === "string" ? parseInt(stageId) : "",
          },
        }
      })
    }
  })

  const [getReservationInitialData] = GraphQLClient.useLazyQuery(GetReservationInitialData, {
    onCompleted: (data) => {
      const item = data.getReservationInitialData
      console.log(item)
      setRemainingSeats(item.remainingSeats)
      setGuestTypes([{ id: 0, name: "一般", stageId, }, ...item.guestTypes])
      setSeatTypes([...getDefaultSeatTypes(stageId || -1), ...item.seatTypes])
      setStaffs([{ id: 0, name: "-", phonetic: "-", productionId: production?.id, stageId: stageId }, ...item.staffs])
      setPrices(item.prices)
      setSeats(item.seats)
      setProductionName(item.productionName)
      setPayments(item.payments)
      setStage(item.stage)
      snackBar.onQuerySuccess()

      const specialGroupDetails = item.guestTypes.map((guestType: GuestType) => {
        return {
          type: guestType.id,
          name: guestType.name,
          size: 0,
        };
      });
      setSpecialGroupDetails(specialGroupDetails);
    },
    onError: (err) => {
      console.log(err)
      snackBar.onQueryError()
    }
  })
  useQuery([stageId], async () => {
    if(stageId) {
      snackBar.loadStart()
      await getReservationInitialData({
        variables: {
          stageId,
        }
      })
    }
  })

  const [createVipReservation] = GraphQLClient.useMutation(CreateVipReservation, {
    onCompleted: (data) => {
      // console.log({data})
      setResponse(data.createVipReservation)
    },
    onError: (err) => {
      // console.log({err})
      setResponse({
        ok: false,
      })
    }
  })

  const [getAllSeatsOfStage] = GraphQLClient.useLazyQuery(GetAllSeatsOfStage, {
    onCompleted: (data) => {
      setSeats(data.getAllSeatsOfStage)
      snackBar.onQuerySuccess()
    },
    onError: (err) => {
      console.log(err)
      snackBar.onQueryError()
    }
  })
  const refreshSeats = useCallback(async () => {
    setSelectedSeatIndexes([])
    await getAllSeatsOfStage({
      variables: {
        stageId,
      }
    })
  }, [getAllSeatsOfStage, stageId])


  const [guestInfo, setGuestInfo] = useState({} as GuestInfo);

  const watchGuestInfo = useCallback(
    (guestInfo: GuestInfo) => {
      const newSpecialGroupDetails: SpecialGroupDetail[] = [
        ...specialGroupDetails,
      ];
      guestInfo.specialGroupSizes.forEach((size, index) => {
        if (newSpecialGroupDetails[index]) {
          newSpecialGroupDetails[index].size = size;
        }
      });
      setSpecialGroupDetails(newSpecialGroupDetails);
    },
    [specialGroupDetails],
  );

  const totalGroupSize = useMemo(() => {
    let totalGroupSize = guestInfo.groupSize;
    if (guestInfo.specialGroupSizes) {
      guestInfo.specialGroupSizes.forEach((specialGroupSize) => {
        totalGroupSize += specialGroupSize;
      });
    }

    return totalGroupSize;
  }, [guestInfo.groupSize, guestInfo.specialGroupSizes]);

  const onSeatSelected = useCallback((seat: Seat) => {
    const index = seats.findIndex(item => item.row === seat.row && item.column === seat.column)
    if(selectedSeatIndexes.includes(index)) {
      setSelectedSeatIndexes(selectedSeatIndexes.filter(i => i !== index))
    } else {
      if(selectedSeatIndexes.length < totalGroupSize) {
        setSelectedSeatIndexes([...selectedSeatIndexes, index])
      }
    }
  }, [seats, selectedSeatIndexes, totalGroupSize])

  const selectedSeatsForSubmit = useMemo(() => {
    return seats.filter((seat, index) => selectedSeatIndexes.includes(index))
  }, [seats, selectedSeatIndexes])

  const onSeatsSubmit = useCallback(() => {
    setPhase(2)
  }, [])

  const disableSeatSelect = useMemo(() => {
    return !stage || (!!stage.disableSeatSelect && !isVip)
  }, [isVip, stage])


  const [selectedValues, setSelectedValues] = useState({} as SelectedValues);

  const [usePoint, setUsePoint] = useState(0);
  const [response, setResponse] = useState({ ok: false })

  const [submitReservation] = GraphQLClient.useMutation(SubmitReservation, {
    onCompleted: (data) => {
      // console.log({data})
      setResponse(data.submitReservation)
    },
    onError: (err) => {
      console.log(err)
      setResponse({
        ok: false,
      })
    }
  });

  const onSubmitAll = useCallback(
    async (point: number, finalPrice: number, payment: number) => {
      if(isVip) {
        if(reservation && selectedSeatsForSubmit.length) {
          await createVipReservation({
            variables: {
              input: {
                reservationId: reservation.id,
                userId: -1,
                seat: selectedSeatsForSubmit[0].id,
                authToken: serial,
              }
            }
          })
          setPhase(4)
          return
        } else {
          setResponse({
            ok: false
          })
        }
      }
      

      setUsePoint(point);

      const guests: {}[] = [];
      Object.keys(selectedValues).forEach((guestType) => {
        const value = selectedValues[parseInt(guestType)];
        const array = Object.keys(value).map((index) => {
          const seatId = value[parseInt(index)];

          const seat = selectedSeatsForSubmit.find(
            (changedSeat) => changedSeat.id === seatId,
          );
          const seatType = seat
            ? seatTypes?.find((type) => type.id === seat.type)?.id || 0
            : 0;
          return {
            guestType: parseInt(guestType),
            seatType,
            seatId,
          };
        });
        guests.push(...array);
      });

      const submitData: any = {
        guests,
        seats: selectedSeatsForSubmit.map((seat) => seat.id),
        reservationData: {
          ...guestInfo,
          lineId: undefined,
          finalPrice,
          usePoint,
          stageId:
            stageId && typeof stageId === "string"
              ? parseInt(stageId)
              : (stageId as any),
          payment,
        },
      };
      if(serial) {
        submitData.authToken = serial
      }
      if (!guests.length) {
        const tempGuests = specialGroupDetails.map((detail) => ({
          guestType: detail.type,
          seatType: 0,
          seatId: -1,
        }));
        guests.push(...tempGuests);

        for (let i = 0; i < guestInfo.groupSize; i++) {
          guests.push({
            guestType: 0,
            seatType: 0,
            seatId: -1,
          });
        }
      }

      await submitReservation({
        variables: {
          input: submitData,
        },
      });
      setPhase(4)
    },
    [selectedSeatsForSubmit, createVipReservation, guestInfo, isVip, reservation, seatTypes, selectedValues, serial, specialGroupDetails, stageId, submitReservation, usePoint],
  );

  const priceIfSeatSelectDisabled = useMemo(() => {
    const result: { [guestType: string]: number } = {};
    result[0] = guestInfo.groupSize;

    Object.keys(specialGroupDetails).forEach((key) => {
      const detail = specialGroupDetails[parseInt(key)];
      result[detail.type] = detail.size;
    });
    return result;
  }, [guestInfo.groupSize, specialGroupDetails]);


  const [phase, setPhase] = useState(0);

  const submitPrice = useCallback((selectedValues: SelectedValues) => {
    setSelectedValues(selectedValues);
    setPhase(3);
  }, []);

  const jumpPhase = useCallback(
    (toPhase: number) => {
      if (toPhase < phase && phase < 4) {
        setPhase(toPhase);
      }
    },
    [phase],
  );

  const stepper = useMemo(() => {
    return disableSeatSelect ? (
      <Stepper activeStep={phase >= 2 ? phase - 1 : phase}>
        <Step completed={phase > 0}>
          <StepButton
            color="inherited"
            onClick={() => {
              if(reservation) {
                return
              }
              setSelectedSeatIndexes([])
              jumpPhase(0);
            }}
          >
            <div style={{ width: "32px" }}>情報入力</div>
          </StepButton>
        </Step>
        <Step completed={phase > 3}>
          <StepButton
            color="primary"
            onClick={() => {
              jumpPhase(3);
            }}
          >
            <div style={{ width: "32px" }}>確認画面</div>
          </StepButton>
        </Step>
      </Stepper>
    ) : (
      <Stepper activeStep={phase}>
        <Step completed={phase > 0}>
          <StepButton
            color="inherited"
            onClick={() => {
              if(reservation) {
                return
              }
              setSelectedValues({});
              setSelectedSeatIndexes([])
              jumpPhase(0);
            }}
          >
            <div style={{ width: "32px" }}>情報入力</div>
          </StepButton>
        </Step>
        <Step completed={phase > 1}>
          <StepButton
            color="primary"
            onClick={() => {
              setSelectedValues({});
              jumpPhase(1);
            }}
          >
            <div style={{ width: "32px" }}>座席選択</div>
          </StepButton>
        </Step>
        <Step completed={phase > 2}>
          <StepButton
            color="primary"
            onClick={() => {
              jumpPhase(2);
            }}
          >
            <div style={{ width: "32px" }}>座席割振</div>
          </StepButton>
        </Step>
        <Step completed={phase > 3}>
          <StepButton
            color="primary"
            onClick={() => {
              jumpPhase(3);
            }}
          >
            <div style={{ width: "32px" }}>確認画面</div>
          </StepButton>
        </Step>
      </Stepper>
    );
  }, [disableSeatSelect, jumpPhase, phase, reservation]);

  const component = useMemo(() => {
    return (
      <ReservationStyle>
        <div className="title">
          <div className="production-name">
            {productionName ?? ""}
          </div>
          <div className="stage-name">{stage ? stage.stageName : ""}</div>
          <div className="stage-time">
            (
            {stage
              ? dayjs(stage.openDateTime * 1000).format("YYYY/MM/DD HH:mm開場")
              : ""}
            )
          </div>
          <div className="display-name">予約画面</div>
          {disableSeatSelect ? (
            <div className="remaining-seats">残り{remainingSeats}席</div>
          ) : (
            <></>
          )}
        </div>
        <div className="stepper">{stepper}</div>
        {phase === 0 ? (
          <GuestInformation
            activateInvitation={true}
            onSubmit={(value) => {
              setGuestInfo(value);
              watchGuestInfo(value);
              setPhase(disableSeatSelect ? 3 : 1);
            }}
            specialGroupDetails={specialGroupDetails}
            staffList={staffs}
            email={guestInfo.email}
            groupSize={guestInfo.groupSize}
            invitationId={guestInfo.invitationId}
            name={guestInfo.name}
            note={guestInfo.note}
            phonetic={guestInfo.phonetic}
            isVip={isVip}
          />
        ) : (
          <></>
        )}
        {phase === 1 ? (
          <SeatSelectArea 
            refreshSeats={refreshSeats}
            seatTypes={seatTypes}
            selectedSeatIndexes={selectedSeatIndexes}
            totalGroupSize={totalGroupSize}
            seats={seats}
            onSubmit={onSeatsSubmit}
            onSeatSelected={onSeatSelected}
            guestTypes={guestTypes}
            prices={prices}
          />
        ) : (
          <></>
        )}
        {phase === 2 ? (
          <WhoGonnaSitWhere
            changedSeats={selectedSeatsForSubmit}
            guestInfo={guestInfo}
            prices={prices}
            seatTypes={seatTypes}
            guestTypes={guestTypes}
            specialGroupDetails={specialGroupDetails}
            selectedValues={selectedValues}
            onSubmit={submitPrice}
            stageId={stageId}
          />
        ) : (
          <></>
        )}
        {phase === 3 ? (
          <Confirm
            changedSeats={selectedSeatsForSubmit}
            agreements={stage?.caution ? [stage.caution] : []}
            onConfirmed={onSubmitAll}
            ownPoint={0}
            seatTypes={seatTypes}
            guestTypes={guestTypes}
            prices={prices}
            selectedValues={selectedValues}
            priceListIfSeatSelectDisabled={priceIfSeatSelectDisabled}
            seatSelectDisabled={
              disableSeatSelect
            }
            payments={payments}
          />
        ) : (
          <></>
        )}
        {
          phase === 4 ? (
            <Complete 
              response={response}
            />
          ) : <></>
        }
      </ReservationStyle>
    );
  }, [productionName, stage, disableSeatSelect, remainingSeats, stepper, phase, specialGroupDetails, staffs, guestInfo, isVip, refreshSeats, seatTypes, selectedSeatIndexes, totalGroupSize, seats, onSeatsSubmit, onSeatSelected, guestTypes, prices, selectedSeatsForSubmit, selectedValues, submitPrice, stageId, onSubmitAll, priceIfSeatSelectDisabled, payments, response, watchGuestInfo]);


  const reservationPeriod = useMemo(() => {
    if(!stage) {
      return false
    }
    const reservationStarted = dayjs.unix(stage.reservationStart).isBefore(dayjs())
    const reservationEnded = dayjs.unix(stage.reservationLimit).isBefore(dayjs())

    return reservationStarted && !reservationEnded
  }, [stage])

  return reservationPeriod ? (
    <>
      {component}
      {snackBar.component}
    </>
    ) : (
    <ReservationStyle>
      {snackBar.component}
    </ReservationStyle>
  );
};

export default NewReservation;

const ReservationStyle = styled.div`
padding: 16px 0 16px 0;
  .title {
    width: 100%;
    margin: 0 auto 20px auto;
    text-align: center;
    .production-name {
      font-size: 20px;
    }
    .stage-name {
      font-size: 16px;
    }
    .stage-time {
      font-size: 12px;
    }
    .display-name {
      font-size: 20px;
    }
  }
  .stepper {
    margin: 10px auto 20px auto;
    background-color: white;
    padding: 10px 0 10px 0;
  }
`;
